import { Fragment, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../data";
import { loadLifecycle, loadReadOnlyState } from "../pages/agentur/einstellungen/data";
import { AgenturSperre, CreatorSperre } from "./Sperrseite";
import { PortalSperreContext } from "./sperreKontext";
import { creatorSperreErlaubt, portalZustand, sperreErlaubt, sperreTitel } from "./sperre-model";
import { NurLesenRahmen } from "./nurLesen";
import { ZweiFaktorPflichtHinweis } from "./ZweiterFaktor";
import { InternesKontoHinweis } from "./InternesKonto";
import { nurInternesKonto } from "../lib/bereich";
import { Link, Navigate, Outlet, useLocation, useNavigate, useSearchParams } from "react-router";
import { PortalLayout, type NavItem } from "../components/shell/PortalLayout";
import { istVollbild, merkeArbeitsseite } from "../pages/agentur/einstellungen/baum";
import { EinstellungenKopf } from "../pages/agentur/einstellungen/Uebersicht";
import { TarifKarte, useTarifKatalog, useTarifKurzstand } from "../components/Tarif";
import { upgradeText, wechselLink } from "../model/tarif";
import { tarifMitPreis } from "../model/testende";
import { EigenerKopf } from "../components/shell/Logo";
import { ErrorState, NoAccess } from "../components/States";
import { StartLaden } from "../components/Ladeschirm";
import { Dialog } from "../components/ui";
import { date, initials, money, setDisplayLocale, setDisplayTimeZone, setOrgLocale } from "../lib/format";
import { embedLang, LangContext, pickLang, setCreatorKonto, setDateFormat, setSchweizerSchreibung, setUiLang, t } from "../lib/sprache";
import { dateOf } from "../pages/creator/lang";
import {
  StaffWorkspaceContext, TalentWorkspaceContext, staffWorkspace, talentWorkspace, useAccount, useArchivedWorkspaces, useSwitchStaffOrg, useSwitchTalentOrg, useWorkspaceChoice,
  type Account, type StaffWorkspace, type TalentWorkspace,
} from "./session";
import { useAgencyUnreadCount, useCreatorUnreadCount, useUnreadByOrg } from "../pages/kommunikation/nachrichten/unread";
import { ChatHinweise } from "../pages/kommunikation/nachrichten/hinweis";
import { MiniChat } from "../pages/kommunikation/nachrichten/MiniChat";
import { CREATOR_AREA_LINK, agencyBottom, agencyNav, agencyPalette, creatorBereichsWechsel, creatorBottom, creatorNav, creatorPalette, studioBottom, studioNav, studioPalette, studioUmleitung, teamBereichsWechsel } from "./nav";
import { arbeitsbereiche, bereichAusAdresse, bereichName, brauchtBereichswahl } from "./bereichswahl-model";
import { Bereichswahl } from "./Bereichswahl";
import { nimmIgRuecksprung } from "./ig-ruecksprung";
import { CreatorLangContext, tr, useCreatorPrefs } from "../pages/creator/sprache";
import { InstagramErinnerung } from "../pages/creator/InstagramErinnerung";
import { hasUnsaved } from "../pages/creator/ungespeichert";
import { AgenturHinweise, CreatorZustimmungen, GesperrtHinweis, useTestendeWeiter } from "../pages/agentur/einstellungen/Lebenszyklus";
import { AdresseHinweis } from "../pages/konto/AdresseHinweis";
import { SupportZeile, ZahlungsmethodeStart } from "../pages/agentur/einstellungen/ZahlungsmethodeStart";
import { aboStand, billingAvailable, billingChange, pauseBeenden } from "../pages/agentur/einstellungen/data";
import { meldeAbo, meldeTestMitKarte } from "../lib/einwilligung";
import { toast } from "../components/Toast";
import { AgenturEinfuehrung, CocoRundgangAgentur } from "../pages/agentur/Einfuehrung";
import { nimmEinrichtungNachZahlung } from "../pages/agentur/einstellungen/nachZahlung";
import { StartRahmen } from "../components/shell/StartRahmen";
import { EinladungsHinweis, OffeneEinladungen } from "../pages/creator/OffeneEinladungen";
import { StartBanner, StartTeil } from "../pages/agentur/Startleitfaden";
import { DemoVideoHinweis } from "../pages/agentur/DemoVideoHinweis";
import { BeispielHinweis } from "../pages/agentur/Schnellstart";
import { agenturSuche } from "./suche";
import { ZahlungsmethodeAendern } from "../pages/konto/ZahlungsmethodeAendern";
import { usePresencePing } from "../lib/presence";
import { setFehlerAgentur } from "../lib/fehlerbericht";
import { setMeldenRolle } from "../lib/melden";
import { kalenderMerkKey, letzterBesuch, zaehleNeueTermine } from "../pages/kommunikation/kalender/neu";
import { startZiel } from "./startziel";

/** Zuletzt genutzter Bereich (Team oder Creator) für die Startseite bei Doppelrolle (Befund 16). */
function rememberArea(area: "agentur" | "creator") {
  try {
    localStorage.setItem("tk-area", area);
  } catch {
    /* ohne Speicher: Start wählt das Team */
  }
}
function lastArea(): string | null {
  try {
    return localStorage.getItem("tk-area");
  } catch {
    return null;
  }
}

/** Rahmen je Bereich mit Zugangsprüfung. Die Rechte selbst prüft immer die Datenbank. */

function RequireLogin({ children }: { children: (a: NonNullable<ReturnType<typeof useAccount>["account"]>) => React.ReactNode }) {
  const { session, account, loading, error } = useAccount();
  const location = useLocation();
  // Agenturwechsel zeichnet den Bereich sofort neu (Befund 4)
  useWorkspaceChoice();
  if (loading) return <StartLaden />;
  // Die Konsole hat ihre eigene Anmeldung (/intern/anmelden); hier gibt es nur die Anmeldung des Kundenportals
  if (!session) return <Navigate to={`/anmelden?weiter=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (error || !account) return <ErrorState error={error ?? new Error(t("Konto konnte nicht geladen werden.", "The account could not be loaded."))} />;
  return <>{children(account)}</>;
}

/** Abmelden aus dem Kontomenü: Sitzung beenden und zur Anmeldung (Praxistest K-078) */
const abmelden = () => { void api.signOut().catch(() => undefined).finally(() => { window.location.assign("/anmelden"); }); };

/** Eigene Seite „Zahlungsmethode ändern“ (/zahlungsmethode): Ziel der Links aus den Zahlungsmails von Stripe; verlangt die Anmeldung */
export function ZahlungsmethodeArea() {
  return <RequireLogin>{(account) => <ZahlungsmethodeAendern account={account} onSignOut={abmelden} />}</RequireLogin>;
}

/**
 * S2 (0540): Ziele des Bereichswechsels tragen ?bereich=<Konto>. Vor dem Rahmen die Wahl für dieses Fenster übernehmen und die
 * Adresse aufräumen; ein Bereich, den die Person nicht hat, wird nur aus der Adresse entfernt.
 */
function BereichAusAdresse({ account, art, children }: { account: Account; art: "team" | "agentur"; children: React.ReactNode }) {
  const [params, setParams] = useSearchParams();
  const roh = params.get("bereich");
  const ziel = bereichAusAdresse(`?${params.toString()}`, art, account);
  const switchStaff = useSwitchStaffOrg();
  const switchTalent = useSwitchTalentOrg();
  useEffect(() => {
    if (!roh) return;
    if (ziel) (art === "team" ? switchStaff : switchTalent)(ziel);
    const next = new URLSearchParams(params);
    next.delete("bereich");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roh]);
  if (roh) return <StartLaden />;
  return <>{children}</>;
}

export function AgencyArea() {
  return (
    <RequireLogin>
      {(account) => <BereichAusAdresse account={account} art="team">{(() => {
        const ws = staffWorkspace(account);
        // Mitglied, aber keine Agentur ist lesbar: nur dann der Sperrhinweis (gesperrt oder gekündigt, 0042; RECHTE-4)
        // J4 (Konto-Prüfung 03.11.2026): wer zusätzlich einen Creator-Zugang hat, landet im Creator-Portal statt auf der Sperrseite
        if (!ws && account.memberships[0]) return account.talentLinks.length > 0 ? <Navigate to="/creator" replace /> : <GesperrtHinweis orgId={account.memberships[0].org_id} />;
        // Ohne jede Mitgliedschaft: Creator ins Creator-Portal, alle anderen in den Schnellstart (Agentur anlegen, dann
        // Zahlung). Etwa nach „Anmelden mit Google“ ohne vorherige Registrierung; keine Sackgasse mehr (Inhaber 03.10.2026).
        // Reines Mitarbeiterkonto (Inhaber 03.11.2026): nicht in die Einrichtung „Wie heißt deine Agentur?“, sondern Hinweis auf
        // den internen Bereich und Abmelden im Kundenportal
        if (!ws && nurInternesKonto(account)) return <InternesKontoHinweis />;
        if (!ws && account.talentLinks.length > 0) return <Navigate to="/creator" replace />;
        if (!ws) return <Navigate to="/einrichtung" replace />;
        return <AgencyFrame account={account} ws={ws} />;
      })()}</BereichAusAdresse>}
    </RequireLogin>
  );
}

/**
 * Zähler am Menüpunkt Kalender (PARITAET-19, AD-192/TA-125): neue Termine der anderen Seite seit dem letzten Besuch des Kalenders.
 * Im Kalender selbst kein Zähler (dort stehen die Termine als „Neu“); nach dem Besuch zählt er ab dem neuen Zeitpunkt.
 */
function useNeueTermine(orgId: string, perspektive: "agentur" | "creator", merkKey: string, enabled = true): number {
  const { pathname } = useLocation();
  const imKalender = pathname.startsWith(`/${perspektive === "agentur" ? "agentur" : "creator"}/kalender`);
  const seit = letzterBesuch(merkKey);
  const q = useQuery({
    queryKey: ["kalender_neu", orgId, perspektive, seit], enabled: enabled && !imKalender && !!seit, staleTime: 60_000,
    queryFn: () => zaehleNeueTermine(api, { orgId, perspektive, seit }),
  });
  return imKalender ? 0 : q.data ?? 0;
}


/**
 * Rückkehr von PayPal (0173): PayPal hängt ?paypal=erfolg&subscription_id=I-… an; das Portal übernimmt das Abo sofort
 * (paypal_confirm), statt auf den Webhook zu warten, und räumt die Adresse danach auf.
 */
function usePaypalRueckkehr(orgId: string, isChef: boolean) {
  const loc = useLocation();
  const qc = useQueryClient();
  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    const id = q.get("subscription_id");
    // K-389: Tarifwechsel mit PayPal: Rückkehr nach der Zustimmung (?paypal=wechsel) bzw. nach Abbruch (?paypal=abgebrochen); bei
    // Abbruch die vorab gespeicherte Vormerkung zurücknehmen, sonst hinge sie fest
    const pp = q.get("paypal");
    if (isChef && (pp === "wechsel" || pp === "abgebrochen") && billingAvailable(api)) {
      const schritt = pp === "abgebrochen"
        ? billingChange(api, orgId, { action: "cancel_pending" }).then(() => toast(t("Der Tarifwechsel bei PayPal wurde abgebrochen. Es bleibt beim bisherigen Tarif.", "The plan change at PayPal was cancelled. The current plan stays."), "status"))
        : Promise.resolve(toast(t("Zustimmung bei PayPal erhalten. Der neue Tarif gilt ab der nächsten Abbuchung.", "Approval received from PayPal. The new plan applies from the next charge."), "status"));
      void schritt.catch(() => undefined).finally(() => {
        void qc.invalidateQueries({ queryKey: ["org_lifecycle", orgId] });
        void qc.invalidateQueries({ queryKey: ["abo", orgId] });
        window.history.replaceState(null, "", loc.pathname);
      });
      return;
    }
    if (!isChef || pp !== "erfolg" || !id || !/^I-[A-Z0-9]{8,40}$/.test(id) || !billingAvailable(api)) return;
    void billingChange(api, orgId, { action: "paypal_confirm", subscription_id: id }).catch(() => undefined).finally(() => {
      void qc.invalidateQueries({ queryKey: ["org_lifecycle", orgId] });
      void qc.invalidateQueries({ queryKey: ["abo", orgId] });
      window.history.replaceState(null, "", loc.pathname);
    });
  }, [loc.search, orgId, isChef]);
}

/**
 * Rückkehr aus dem Stripe-Checkout (0180): Stripe hängt ?checkout=erfolg&session_id=cs_… an; das Portal übernimmt den Abschluss
 * selbst (checkout_confirm), falls der Webhook ausbleibt oder später kommt. Gleiche Prüfungen wie der Webhook, doppelt harmlos.
 */
function useStripeRueckkehr(orgId: string, isChef: boolean): () => void {
  const loc = useLocation();
  const qc = useQueryClient();
  const bestaetigen = () => {
    const q = new URLSearchParams(loc.search);
    const id = q.get("session_id");
    if (!isChef || q.get("checkout") !== "erfolg" || !id || !/^cs_(test|live)_[A-Za-z0-9]{10,200}$/.test(id) || !billingAvailable(api)) {
      void qc.invalidateQueries({ queryKey: ["org_lifecycle", orgId] });
      return;
    }
    void billingChange(api, orgId, { action: "checkout_confirm", session_id: id }).catch(() => undefined).finally(() => {
      void qc.invalidateQueries({ queryKey: ["org_lifecycle", orgId] });
      void qc.invalidateQueries({ queryKey: ["abo", orgId] });
    });
  };
  useEffect(bestaetigen, [loc.search, orgId, isChef]);
  return bestaetigen;
}

/** Wie lange die App nach der Rückkehr aus Stripe nachfragt, bevor sie einen Hinweis zeigt (0180) */
const BESTAETIGUNG_WARTEN_MS = 60_000;

/** Eigene Komponente, damit der Zähler der Glocke (Hook) laufen darf. */
function AgencyFrame({ account, ws }: { account: Account; ws: StaffWorkspace }) {
  // Oberflächensprache je Person (PARITAET-8): eigene Wahl im Profil → Sprache der Agentur → Deutsch
  const lang = pickLang(embedLang(), account.language, ws.org.locale);
  setUiLang(lang);
  setCreatorKonto(!!ws.isCreator);
  setDateFormat(account.dateFormat);
  setDisplayLocale(lang === "en" ? "en-GB" : ws.org.locale);
  setOrgLocale(ws.org.locale);
  setSchweizerSchreibung(lang === "de" ? ws.org.locale : null);
  setDisplayTimeZone(ws.org.timezone);
  setFehlerAgentur(ws.org.org_id);
  // 0512 (J1): ohne „Beträge sehen“ liest und schreibt das Portal Kooperationen über die Sichten ohne Geldwerte
  api.betraegeVerborgen?.(!ws.isChef && !ws.can("view_amounts"));
  setMeldenRolle(ws.isChef ? "chef_manager" : ws.membership.role);
  useEffect(() => rememberArea("agentur"), []);
  const unread = useAgencyUnreadCount(ws.org.org_id);
  usePresencePing(ws.org.org_id, "staff");
  // Nur-Lesen-Phase (Admin und seit 0517 auch Manager lesen mit): Schreibwege zentral gesperrt (RECHTE-8)
  // 0169: nach dem Stripe-Abschluss (Rückkehr mit ?checkout=erfolg) kurz nachfragen, bis der Webhook das Abo eingetragen hat
  const ort = useLocation();
  // IA-1: „Alle Einstellungen“ im Vollbild; die letzte Arbeitsseite merken, damit „Einstellungen schließen“ dorthin zurückführt
  const vollbild = istVollbild(ort.pathname);
  useEffect(() => { merkeArbeitsseite(`${ort.pathname}${ort.search}${ort.hash}`); }, [ort.pathname, ort.search, ort.hash]);
  const suche = new URLSearchParams(ort.search);
  const zurueckVonStripe = suche.get("checkout") === "erfolg" || suche.get("paypal") === "erfolg";
  // Hilfe & Support bleibt auch vor der Zahlung erreichbar
  const hilfeOffen = ort.pathname.startsWith("/agentur/hilfe");
  usePaypalRueckkehr(ws.org.org_id, ws.isChef);
  const stripeBestaetigen = useStripeRueckkehr(ws.org.org_id, ws.isChef);
  // 0180: höchstens eine Minute nachfragen, danach ein Hinweis statt endlosem „wird bestätigt“
  const [wartenSeit, setWartenSeit] = useState(() => Date.now());
  const [zuLange, setZuLange] = useState(false);
  useEffect(() => {
    if (!zurueckVonStripe) return;
    setZuLange(false);
    const timer = window.setTimeout(() => setZuLange(true), BESTAETIGUNG_WARTEN_MS);
    return () => window.clearTimeout(timer);
  }, [zurueckVonStripe, wartenSeit]);
  const life = useQuery({
    queryKey: ["org_lifecycle", ws.org.org_id], queryFn: () => loadLifecycle(api, ws.org.org_id), staleTime: 60_000,
    refetchInterval: (q) => (zurueckVonStripe && q.state.data?.needs_payment_method && Date.now() - wartenSeit < BESTAETIGUNG_WARTEN_MS ? 3000 : false),
  });
  // Erst bezahlen, dann einrichten (Inhaber 03.10.2026): nach dem Abschluss aus dem Schnellstart weiter zur Einrichtung
  const navigate = useNavigate();
  const bezahlt = !!life.data && !life.data.needs_payment_method;
  useEffect(() => {
    // Inhaber 13.10.2026: nach dem Abschluss ins Konto; die Einrichtung (Firmendaten, Konditionen …) bietet die Übersicht an
    if (zurueckVonStripe && bezahlt && ws.isChef && nimmEinrichtungNachZahlung()) navigate("/agentur", { replace: true });
  }, [zurueckVonStripe, bezahlt, ws.isChef]);
  // Conversion nach der Rückkehr (Inhaber 02.11.2026): im Test „Test mit Karte“ (1 €), sonst „Abo abgeschlossen“ mit Preis;
  // einmal je Konto, nur mit Zustimmung und nur auf der echten App
  useEffect(() => {
    if (!zurueckVonStripe || !bezahlt || !ws.isChef) return;
    void aboStand(api, ws.org.org_id).then((st) => (st.imTest ? meldeTestMitKarte(ws.org.org_id) : meldeAbo(ws.org.org_id, st.wertEuro)), () => undefined);
  }, [zurueckVonStripe, bezahlt, ws.isChef, ws.org.org_id]);
  const neueTermine = useNeueTermine(ws.org.org_id, "agentur", kalenderMerkKey(ws.membership.id));
  const withBadge = (items: NavItem[]) => items.map((i) => (i.to === "/agentur/nachrichten" && unread.messages ? { ...i, badge: unread.messages }
    : (i.to === "/agentur/kalender" || i.also?.includes("/agentur/kalender")) && neueTermine ? { ...i, badge: neueTermine, badgeKind: "new" as const } : i));
  // Doppelrolle: wer zugleich Creator ist, erreicht das eigene Portal über die Navigation (Befund 16); im eigenen Creator-Konto
  // heißen die Ziele „Bei <Agentur>“ und stehen zusätzlich im Kopf neben „Mein Konto“ (S2, 0540)
  const wechsel = teamBereichsWechsel(account, ws);
  const bereichsWahl = ws.isCreator && wechsel.length > 0 ? {
    value: ws.org.org_id,
    label: t("Bereich wechseln", "Switch workspace"),
    options: [{ value: ws.org.org_id, label: t("Mein Konto", "My account") }, ...wechsel.map((w) => ({ value: w.to, label: w.label }))],
    onChange: (v: string) => { if (v !== ws.org.org_id) navigate(v); },
  } : undefined;
  // K-370/K-371: Tarif im Kontomenü und in der Tarifkarte; Upgrade nur für Admins mit größerem buchbarem Tarif
  const tarif = useTarifKurzstand(ws, life.data);
  // K-486: auf der Seite „Nur lesen“ weiter im bisherigen Tarif (Monatsbetrag, bei jährlicher Abrechnung umgerechnet)
  const katalog = useTarifKatalog();
  const bisher = tarifMitPreis(life.data?.plan_key ?? null, katalog.data ?? null, life.data?.billing_interval === "year" ? "year" : "month");
  const bisherMonat = bisher && bisher.cents !== null ? Math.round(bisher.interval === "year" ? bisher.cents / 12 : bisher.cents) : null;
  const weiterNurLesen = ws.isChef && !ws.isCreator && bisher
    ? { to: wechselLink(bisher.key), label: bisherMonat !== null
      ? t(`Weiter mit ${bisher.name} für ${money(bisherMonat, "EUR")} netto im Monat`, `Continue with ${bisher.name} for ${money(bisherMonat, "EUR")} net per month`)
      : t(`Weiter mit ${bisher.name}`, `Continue with ${bisher.name}`) }
    : null;
  // 0323: Testende ohne Zahlungsart: Weg zum empfohlenen Tarif auf der Seite „Nur lesen“ (nur Admin)
  const testendeWeiter = useTestendeWeiter(ws.org.org_id, life.data, ws.isChef);
  // 0414 (P-4): laufende Abo-Pause: Sperrseite und gesperrte Bereiche nennen das Ende, der Admin beendet sie dort vorzeitig
  const qcPause = useQueryClient();
  const pauseNurLesen = life.data?.reason === "paused"
    ? { bis: life.data.pause?.ends_at ? date(life.data.pause.ends_at, ws.org.timezone) : t("zum Ende der Pause", "the end of the pause"),
        beenden: ws.isChef && !life.data.cancel_effective_at ? async () => { await pauseBeenden(api, ws.org.org_id); await qcPause.invalidateQueries(); toast(t("Pause beendet. Willkommen zurück.", "Pause ended. Welcome back.")); } : null }
    : null;
  // D-2: steht die Testleiste, läuft der nächste Startschritt in ihr mit; sonst eigenes schmales Startbanner
  const [testLeisteDa, setTestLeisteDa] = useState(false);
  // 0425 (Inhaber 22.10.2026): Vertrag beendet → Portal gesperrt, Sperrseite statt Portal; Pause und offene Zahlung bleiben nur lesbar
  const roStand = useQuery({ queryKey: ["org_read_only", ws.org.org_id], enabled: !!life.data?.status_read_only, staleTime: 60_000,
    queryFn: () => loadReadOnlyState(api, ws.org.org_id) });
  const gesperrt = !!life.data?.status_read_only && portalZustand(roStand.data) === "locked";
  // Z-5 (Prüf-Chat 27.10.2026): solange der Kontostand lädt, nicht schon das Portal zeigen (sonst kurz Schnellstart samt Menü vor
  // der Tarifwahl, obwohl noch keine Zahlungsart hinterlegt ist)
  if (life.isPending) return <StartLaden />;
  // Vor dem Abschluss (Inhaber 13.10.2026): nur Tarifwahl und Zahlung im schlanken Rahmen, ohne Portal-Menü; Hilfe bleibt erreichbar
  if (life.data?.needs_payment_method) {
    const leben = life.data;
    return (
      <LangContext.Provider value={lang}>
        <StartRahmen title={t("Tarif wählen", "Choose a plan")} schritt={2} onSignOut={abmelden}>
          <StaffWorkspaceContext.Provider value={ws}>
            <Fragment key={`${ws.org.org_id}-${lang}`}>
              {hilfeOffen
                ? <><div className="row"><Link className="btn" to="/agentur">{t("Zurück zur Tarifwahl", "Back to plan choice")}</Link></div><Outlet /></>
                : (zurueckVonStripe
              ? (zuLange
                ? (
                  <div className="panel stack tk-empty" role="status">
                    <h1 className="tk-state-title">{t("Die Bestätigung dauert länger", "The confirmation is taking longer")}</h1>
                    <p>{t("Der Abschluss ist noch nicht bestätigt. Wurde die Zahlung angenommen, kommt die Bestätigung von selbst. Prüfe gleich noch einmal oder lade die Seite später neu, und schließe bitte nicht ein zweites Mal ab.",
                      "The checkout has not been confirmed yet. If the payment was accepted, the confirmation arrives by itself. Check again now or reload the page later, and please do not subscribe a second time.")}</p>
                    <div className="row">
                      <button type="button" className="btn" onClick={() => { setWartenSeit(Date.now()); stripeBestaetigen(); }}>{t("Erneut prüfen", "Check again")}</button>
                    </div>
                    <SupportZeile />
                  </div>
                )
                : <div className="panel stack tk-empty" role="status" aria-busy="true"><h1 className="tk-state-title">{t("Zahlungsart wird bestätigt …", "Confirming payment method …")}</h1><p>{t("Das dauert meist nur wenige Sekunden.", "This usually takes only a few seconds.")}</p></div>)
              : <ZahlungsmethodeStart orgId={ws.org.org_id} isChef={ws.isChef} life={leben} timeZone={ws.org.timezone} />)}
              <CreatorPortalWechsel account={account} />
            </Fragment>
          </StaffWorkspaceContext.Provider>
        </StartRahmen>
      </LangContext.Provider>
    );
  }
  if (life.data?.status_read_only && roStand.isLoading) return <StartLaden />;
  if (gesperrt) {
    // Hinter der Sperre erreicht der Admin nur Export, Abo & Tarif (neues Abo, Löschung) und Hilfe, im schlanken Rahmen ohne Portal-Menü
    const offen = sperreErlaubt(ort.pathname);
    const fmt = (iso: string) => date(iso, ws.org.timezone);
    return (
      <LangContext.Provider value={lang}>
        <StartRahmen title={sperreTitel(life.data?.reason, !!roStand.data?.trial)} onSignOut={abmelden}>
          <StaffWorkspaceContext.Provider value={ws}>
            <Fragment key={`${ws.org.org_id}-${lang}-sperre`}>
              {offen ? (
                <PortalSperreContext.Provider value>
                  <div className="row"><Link className="btn" to="/agentur">‹ {t("Zurück zur Übersicht", "Back to overview")}</Link></div>
                  <NurLesenRahmen aktiv><Outlet /></NurLesenRahmen>
                </PortalSperreContext.Provider>
              ) : (
                <AgenturSperre orgId={ws.org.org_id} orgName={ws.org.name} life={life.data} chef={ws.isChef} fmt={fmt} trial={!!roStand.data?.trial}
                  weiter={testendeWeiter ?? weiterNurLesen} />
              )}
              <CreatorPortalWechsel account={account} />
            </Fragment>
          </StaffWorkspaceContext.Provider>
        </StartRahmen>
      </LangContext.Provider>
    );
  }
  // Studio-Texte (22.10.2026): Seiten, die es im Creator-Konto nicht gibt (Team, Chat, Creator-Liste, Kennzahlen …), leiten um
  const studioZiel = ws.isCreator ? studioUmleitung(ort.pathname) : null;
  if (studioZiel) return <Navigate to={studioZiel} replace />;
  return (
    <LangContext.Provider value={lang}>
    <PortalLayout
      lang={lang}
      brandHref="/agentur"
      orgName={ws.org.name}
      accent={ws.org.brand_color}
      nav={withBadge([...(ws.isCreator ? studioNav() : agencyNav(ws.isChef, ws.can("view_financials"), ws.can("trigger_payouts"), ws.can("view_amounts"))), ...wechsel])}
      bottom={withBadge(ws.isCreator ? studioBottom() : agencyBottom(ws.isChef, ws.can("view_financials"), ws.can("trigger_payouts"), ws.can("view_amounts")))}
      palette={[...(ws.isCreator ? studioPalette() : agencyPalette(ws.isChef, ws.can("view_financials"), ws.can("trigger_payouts"), ws.can("view_amounts"))), ...wechsel.map((w) => ({ label: w.label, to: w.to }))]}
      agencyChoice={bereichsWahl}
      suche={(q) => agenturSuche(api, ws.org.org_id, q, { betraege: ws.isChef || ws.can("view_amounts") })}
      bell={{ href: "/agentur/benachrichtigungen", unread: unread.total }}
      gearHref="/agentur/einstellungen"
      vollbild={vollbild ? <EinstellungenKopf orgName={ws.org.name} sicht={{ chef: ws.isChef, creator: !!ws.isCreator }} /> : undefined}
      account={{
        name: ws.name, initials: account.initials ?? initials(ws.name), href: "/agentur/profil", org: ws.org.name, plan: tarif?.name ?? null,
        profileLabel: ws.isCreator ? t("Mein Zugang", "My access") : undefined,
        // IA-1: Persönliches im Profilmenü, Agentur-Themen in „Alle Einstellungen“
        securityHref: "/agentur/einstellungen/sicherheit", notificationsHref: "/agentur/benachrichtigungen#kanaele",
        languageHref: "/agentur/profil#sprache", designHref: "/agentur/profil#darstellung",
        settingsHref: "/agentur/einstellungen", aboHref: ws.isChef ? "/agentur/einstellungen/abo" : undefined, helpHref: "/agentur/hilfe",
      }}
      upgrade={tarif?.admin && tarif.next && tarif.nextName && !tarif.testBis && !tarif.zahlungOffen && life.data?.reason !== "paused" ? { label: upgradeText(tarif.nextName), to: wechselLink(tarif.next) } : null}
      sideExtra={<TarifKarte stand={tarif} timeZone={ws.org.timezone} />}
      onSignOut={abmelden}
    >
      <StaffWorkspaceContext.Provider value={ws}>
        {/* Sprach- und Agenturwechsel zeichnen den Inhalt neu: keine Beschriftung in der alten Sprache, keine Formulare mit
            Kennungen der vorigen Agentur (G-WEB-3) */}
        <Fragment key={`${ws.org.org_id}-${lang}-${account.dateFormat ?? ""}`}>
          {/* 0247: eigene Adresse noch unbestätigt (Anmeldung ohne Code) */}
          <AdresseHinweis />
          {/* D-2 (Inhaber 21.10.2026): Testleiste und nächster Startschritt in EINER schmalen Leiste */}
          <AgenturHinweise orgId={ws.org.org_id} isChef={ws.isChef} timeZone={ws.org.timezone} onTestLeiste={setTestLeisteDa}
            start={!life.data?.read_only ? <StartTeil ws={ws} /> : null} />
          <ZweiFaktorPflichtHinweis orgId={ws.org.org_id} />
          {/* Befund 5b (21.10.2026): im Nur-Lesen-Zustand kein „Als Nächstes: Ersten Creator anlegen“ */}
          {/* IA-1: im Vollbild „Alle Einstellungen“ ohne Startbanner; D-2: Startschritt steckt in der Testleiste, sonst eigene schmale Leiste */}
          {/* Inhaber 03.11.2026: Hinweis auf das Demo-Video beim Einstieg, bis er ausgeblendet wird */}
          {!life.data?.read_only && !vollbild && <DemoVideoHinweis ws={ws} />}
          {!life.data?.read_only && !vollbild && !testLeisteDa && <StartBanner ws={ws} />}
          {/* 0325: Beispieldaten sichtbar als Beispiel, mit einem Klick zu löschen */}
          {!vollbild && <BeispielHinweis ws={ws} />}
          {<><NurLesenRahmen aktiv={!!life.data?.read_only} testende={testendeWeiter} weiter={weiterNurLesen} pause={pauseNurLesen}><Outlet /></NurLesenRahmen><AgenturEinfuehrung nurLesen={life.data ? !!life.data.read_only : true} /><CocoRundgangAgentur ws={ws} nurLesen={life.data ? !!life.data.read_only : true} /></>}
          {/* K-524: Chat auf jeder Seite (nicht im eigenen Creator-Konto, das keinen Chat hat) und Hinweis bei neuer Nachricht */}
          {!ws.isCreator && !vollbild && <><MiniChat orgId={ws.org.org_id} timeZone={ws.org.timezone} /><ChatHinweise /></>}
        </Fragment>
      </StaffWorkspaceContext.Provider>
    </PortalLayout>
    </LangContext.Provider>
  );
}

export function CreatorArea() {
  return (
    <RequireLogin>
      {(account) => <BereichAusAdresse account={account} art="agentur"><CreatorGate account={account} /></BereichAusAdresse>}
    </RequireLogin>
  );
}

/** Wählt das Creator-Profil; frühere Agenturen (archiviert) kommen aus 0045 dazu. */
function CreatorGate({ account }: { account: Account }) {
  const archived = useArchivedWorkspaces(account);
  // S2 (0540): Instagram-Anmeldung aus dem eigenen Creator-Konto kehrt über /creator/instagram zurück; weiter zum eigenen Profil
  const ort = useLocation();
  const [igZiel] = useState(() => (ort.pathname === "/creator/instagram" && /[?&]ig=/.test(ort.search) ? nimmIgRuecksprung() : null));
  if (igZiel) return <Navigate to={`${igZiel}${ort.search}`} replace />;
  if (archived.isLoading) return <StartLaden />;
  const ws = talentWorkspace(account, undefined, archived.data ?? []);
  // Teammitglied ohne Creator-Profil (etwa alter /creator-Link im App-Fenster nach Kontowechsel): in die Agentur statt Sackgasse
  if (!ws && account.memberships.length > 0) return <Navigate to="/agentur" replace />;
  if (!ws && nurInternesKonto(account)) return <InternesKontoHinweis />;
  if (!ws) return <NoAccess text={t("Dieses Konto ist mit keinem Creator-Profil verbunden.", "This account is not connected to a creator profile.")} />;
  return <CreatorFrame account={account} ws={ws} />;
}

function CreatorFrame({ account, ws }: { account: Account; ws: TalentWorkspace }) {
  const userId = account.session.userId;
  const prefs = useCreatorPrefs(userId, ws.link.talent_id, ws.org.default_document_language);
  const lang = embedLang() ?? prefs.lang;
  const igEnabled = prefs.igEnabled;
  setUiLang(lang);
  setCreatorKonto(false);
  setDateFormat(account.dateFormat);
  setDisplayLocale(lang === "en" ? "en-GB" : ws.org.locale);
  setOrgLocale(ws.org.locale);
  // Creator wohnen oft nicht im Land der Agentur: das Creator-Portal bleibt in der allgemeinen Schreibung
  setSchweizerSchreibung(null);
  setDisplayTimeZone(ws.org.timezone);
  setFehlerAgentur(ws.link.org_id);
  setMeldenRolle("creator");
  useEffect(() => rememberArea("creator"), []);
  // Anwesenheit im Creator-Portal dieser Agentur (0048); archivierte Profile melden nichts
  usePresencePing(ws.link.org_id, "creator");
  const switchOrg = useSwitchTalentOrg();
  const [pending, setPending] = useState<string | null>(null);
  const unread = useCreatorUnreadCount(ws.link.talent_id, ws.link.org_id);
  // Nur-Lesen-Phase der Agentur (RECHTE-8): Hinweis und gesperrte Schreibwege; archivierte Profile sind ohnehin nur Belege
  const ro = useQuery({
    queryKey: ["org_read_only", ws.link.org_id],
    enabled: !ws.archived,
    staleTime: 60_000,
    queryFn: () => loadReadOnlyState(api, ws.link.org_id).catch(() => ({ read_only: false, documents_until: null, reason: undefined, paused_until: null, ended: false })),
  });
  const nurLesen = !ws.archived && !!ro.data?.read_only;
  // 0425: Vertrag der Agentur beendet: Bereich der Agentur gesperrt; der eigene Daten-Download unter Konto bleibt (AVV)
  const gesperrt = !ws.archived && portalZustand(ro.data) === "locked";
  const { pathname } = useLocation();
  // Zähler je Agentur im Wechsler: was in der anderen Agentur wartet, ist sichtbar (Befund 7)
  const perOrg = useUnreadByOrg(ws.others.length > 1);
  const neueTermine = useNeueTermine(ws.link.org_id, "creator", kalenderMerkKey(ws.link.talent_id), !ws.archived);
  const withBadge = (items: NavItem[]) => items.map((i) => (i.to === "/creator/nachrichten" && unread.messages ? { ...i, badge: unread.messages }
    : (i.to === "/creator/kalender" || i.also?.includes("/creator/kalender")) && neueTermine ? { ...i, badge: neueTermine, badgeKind: "new" as const } : i));
  const t = (de: string, en: string) => tr(lang, de, en);
  // Bereichswechsel: „Mein Konto“ zum eigenen Creator-Konto (S2, 0540), „Zur Agentur“ für Teammitglieder
  const wechsel = creatorBereichsWechsel(account, lang);
  const eigene = arbeitsbereiche(account).filter((b) => b.art === "eigen");
  const nav = [...creatorNav(lang, { archived: ws.archived, instagram: igEnabled, gesperrt }), ...wechsel];
  const navigate = useNavigate();
  // Ungespeicherte Eingaben (Rechnungsentwurf, Profil): erst nachfragen, dann wechseln; Ziele mit „/“ sind ein anderer Bereich
  const wechseln = (ziel: string) => (ziel.startsWith("/") ? navigate(ziel) : switchOrg(ziel));
  const choose = (orgId: string) => {
    if (orgId === ws.link.org_id) return;
    if (hasUnsaved()) setPending(orgId);
    else wechseln(orgId);
  };
  return (
    <CreatorLangContext.Provider value={lang}>
      <PortalLayout
        lang={lang === "en" ? "en" : "de"}
        brandHref={gesperrt ? "/creator/konto" : ws.archived ? "/creator/rechnungen" : "/creator"}
        orgName={ws.org.name}
        accent={ws.org.brand_color}
        nav={withBadge(nav)}
        bottom={withBadge(creatorBottom(lang, { archived: ws.archived, gesperrt }))}
        palette={[...creatorPalette(lang, { archived: ws.archived, instagram: igEnabled, gesperrt }), ...wechsel.map((w) => ({ label: w.label, to: w.to }))]}
        bell={ws.archived || gesperrt ? undefined : { href: "/creator/benachrichtigungen", unread: unread.total }}
        account={{ name: ws.talentName, initials: account.initials ?? initials(ws.talentName), href: ws.archived || gesperrt ? "/creator/konto" : "/creator/profil", org: ws.org.name, settingsHref: ws.archived || gesperrt ? undefined : "/creator/konto" }}
        onSignOut={abmelden}
        agencyChoice={ws.others.length > 1 || eigene.length > 0 ? {
          value: ws.link.org_id,
          label: eigene.length ? t("Bereich wechseln", "Switch workspace") : undefined,
          options: [
            // S2 (0540): eigenes Creator-Konto als eigener Bereich vor den Agenturen
            ...eigene.map((b) => ({ value: b.to, label: t("Mein Konto", "My account") })),
            ...ws.others.map((o) => {
              const name = eigene.length ? bereichName({ art: "agentur", name: o.name }, t) : o.name;
              // kurz, damit der gewählte Wert in die Kopfzeile passt (SICHT-CREATOR-20); was „archiviert“ heißt, sagt die Seite selbst
              return {
                value: o.org_id,
                label: o.archived ? `${name} (${t("archiviert", "archived")})`
                  : o.org_id !== ws.link.org_id && perOrg[o.org_id] ? `${name} · ${perOrg[o.org_id]} ${t("neu", "new")}` : name,
              };
            }),
          ],
          onChange: choose,
        } : undefined}
      >
        <TalentWorkspaceContext.Provider value={ws}>
          {/* Neue Fassung der Nutzungsbedingungen oder Datenschutzhinweise: auch Creator stimmen zu (Befund 11) */}
          <CreatorZustimmungen lang={lang === "en" ? "en" : "de"} />
          {/* 0340: einmal je Anmeldung „Verbinde dein Instagram“, nur ohne Verbindung und nur mit Freigabe der Agentur */}
          <InstagramErinnerung userId={userId} talentId={ws.link.talent_id} orgId={ws.link.org_id} igEnabled={igEnabled} archived={!!ws.archived || gesperrt} lang={lang} />
          {/* Prüf-Chat CP-001b: offene Einladung einer weiteren Agentur im Portal annehmen, ohne den Link aus der Mail */}
          <EinladungsHinweis lang={lang === "en" ? "en" : "de"} />
          {nurLesen && !gesperrt && (
            <div className="content tk-rahmen-hinweise">
              <div className="integrity-banner" role="status">
                <p><strong>{t("Nur lesen.", "Read-only.")}</strong> {t(
                  `Deine Agentur nutzt CreatorOffice derzeit nur lesend. Nachrichten, Rechnungen und Unterschriften sind nicht möglich; deine Rechnungen, Gutschriften und Verträge kannst du weiter ansehen und herunterladen${ro.data?.documents_until ? `, bis zum ${dateOf(ro.data.documents_until, "de")}` : ""}.`,
                  `Your agency currently uses CreatorOffice read-only. Messages, invoices and signatures are not possible; you can still view and download your invoices, credit notes and contracts${ro.data?.documents_until ? ` until ${dateOf(ro.data.documents_until, "en")}` : ""}.`)}</p>
                <div className="row"><Link className="btn" to="/creator/rechnungen">{t("Belege ansehen", "View documents")}</Link></div>
              </div>
            </div>
          )}
          {/* eigenes Datumsformat (PARITAET-R3-7) und Agenturwechsel (G-WEB-3): Wechsel zeichnet den Inhalt neu */}
          {gesperrt && !creatorSperreErlaubt(pathname)
            ? <CreatorSperre orgName={ws.org.name} belegeBis={ro.data?.documents_until ? dateOf(ro.data.documents_until, lang === "en" ? "en" : "de") : null} tc={t} />
            : <NurLesenRahmen aktiv={nurLesen} creator en={lang === "en"} pause={ro.data?.reason === "paused" && ro.data.paused_until ? { bis: dateOf(ro.data.paused_until, lang === "en" ? "en" : "de") } : null}><Fragment key={`${ws.link.org_id}-${account.dateFormat ?? ""}`}><Outlet /></Fragment></NurLesenRahmen>}
          {/* K-524: Hinweis bei neuer Nachricht der Agentur (nicht auf der Chatseite) */}
          <ChatHinweise />
        </TalentWorkspaceContext.Provider>
      </PortalLayout>
      <Dialog open={pending !== null} onClose={() => setPending(null)} title={t("Arbeitsbereich wechseln?", "Switch workspace?")}>
        <p>{t("Ungespeicherte Änderungen gehen verloren.", "Unsaved changes will be lost.")}</p>
        <div className="row">
          <button className="primary" onClick={() => setPending(null)}>{t("Bleiben", "Stay")}</button>
          <button onClick={() => { const o = pending; setPending(null); if (o) wechseln(o); }}>{t("Wechseln", "Switch")}</button>
        </div>
      </Dialog>
    </CreatorLangContext.Provider>
  );
}

/** Startseite: je nach Konto in den passenden Bereich. */
export function Start() {
  const { session, account, loading, error } = useAccount();
  if (loading) return <StartLaden />;
  if (!session) return <Navigate to="/anmelden" replace />;
  if (error || !account) return <ErrorState error={error} />;
  // S2 (0540): eigenes Creator-Konto und Profil bei einer Agentur: nach der Anmeldung klar wählen, wo es weitergeht
  if (brauchtBereichswahl(account)) return <Bereichswahl account={account} zuletzt={lastArea()} />;
  // Doppelrolle: zuletzt genutzter Bereich (Befund 16). Nie in die Konsole: die hat ihre eigene Anmeldung (/intern/anmelden)
  const ziel = startZiel(account, lastArea());
  if (ziel) return <Navigate to={ziel} replace />;
  if (nurInternesKonto(account)) return <InternesKontoHinweis />;
  // Neues Konto ohne Agentur und ohne Einladung: direkt zur Einrichtung, dort legt man die Agentur an
  return (
    <>
    <EigenerKopf />
    <main id="main" className="content">
      {/* Prüf-Chat CP-001 (02.11.2026): offene Einladungen an die Anmeldeadresse zuerst */}
      <OffeneEinladungen>
      <div className="panel stack tk-empty">
        <h1>{t("Willkommen bei CreatorOffice", "Welcome to CreatorOffice")}</h1>
        <p>{t("Dieses Konto gehört noch zu keiner Agentur. Lege deine Agentur an oder öffne den Einladungslink aus deiner Mail, wenn dich eine Agentur eingeladen hat.", "This account does not belong to an agency yet. Create your agency or open the invitation link from your email if an agency has invited you.")}</p>
        <div className="row">
          <Link className="btn primary" to="/einrichtung/firma">{t("Agentur einrichten", "Set up agency")}</Link>
          <Link className="btn" to="/einladung">{t("Ich habe einen Einladungslink", "I have an invitation link")}</Link>
        </div>
        <AnmeldungLoeschen />
      </div>
      </OffeneEinladungen>
    </main>
    </>
  );
}

/** 0207: Konto ohne Agentur (z. B. nach dem Löschen des Testkontos): die eigene Anmeldung selbst löschen */
function AnmeldungLoeschen() {
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const loeschen = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await api.rpc("account_delete_request", { p_reason: null });
      await api.signOut({ everywhere: true }).catch(() => undefined);
      window.location.assign("/anmelden");
    } catch (e) {
      setFehler(e instanceof Error ? e.message : t("Das hat gerade nicht geklappt.", "That did not work just now."));
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      {!offen
        ? <div className="row"><button type="button" className="quiet" onClick={() => setOffen(true)}>{t("Meine Anmeldung löschen", "Delete my sign-in")}</button></div>
        : (
          <>
            <p>{t("Deine Anmeldung wird geschlossen und danach endgültig gelöscht. Der Zugang endet sofort.", "Your sign-in is closed and then permanently erased. Access ends immediately.")}</p>
            {fehler && <p className="tk-error" role="alert">{fehler}</p>}
            <div className="row">
              <button type="button" className="danger" disabled={busy} aria-busy={busy} onClick={() => void loeschen()}>{busy ? t("Wird gelöscht …", "Deleting …") : t("Anmeldung endgültig löschen", "Delete sign-in permanently")}</button>
              <button type="button" disabled={busy} onClick={() => setOffen(false)}>{t("Abbrechen", "Cancel")}</button>
            </div>
          </>
        )}
    </div>
  );
}

/** J4 (Konto-Prüfung 03.11.2026): Bereichswechsel auf Sperr- und Zahlungsbildschirm, wenn die Person auch Creator ist */
function CreatorPortalWechsel({ account }: { account: Account }) {
  if (!account.talentLinks.length) return null;
  return (
    <p className="muted tk-bereichswechsel">
      {t("Du arbeitest auch als Creator mit einer Agentur:", "You also work as a creator with an agency:")}{" "}
      <Link to={CREATOR_AREA_LINK.to}>{CREATOR_AREA_LINK.label}</Link>
    </p>
  );
}
